/**
 * Contracts of the GENERAL search of the 3.0.0 archive (checklist A9): the page "Ricerca documenti"
 * across every property the caller may search.
 *
 *  - `GET /api/v2/document-archive-docs/search`: the rows matching the filters, at most 20.000;
 *  - `GET /api/v2/document-archive-docs/search/taxonomy`: the areas, sections and document types the
 *    filters of the page offer.
 *
 * Both ask for `canReadDocumentsList`. The search within ONE property has no route: that page filters
 * the tree it already holds (`GET /document-archive-docs/tree`).
 *
 * Shapes copied from the back-end (`document-archive-search.interface.ts`, README of the module,
 * §4.44), which is the source of truth.
 *
 * @since 3.0.0
 */

import { DocumentArchiveDocListItem } from "src/app/shared/interfaces/document-archive-doc.interface";
import { DocumentTemplateTranslation } from "src/app/shared/interfaces/document-template.interface";

/** What `properties` takes, ALONE, for every property the caller may search */
export const ARCHIVE_SEARCH_ALL_PROPERTIES = "all";

/** Longest list of ids each list parameter accepts: past it the back-end answers 400 */
export const ARCHIVE_SEARCH_MAX_LIST = 1000;

/** Longest text `protocollo` and `keyword` accept, measured once trimmed */
export const ARCHIVE_SEARCH_MAX_TEXT = 100;

/** Most rows one search answers with: above it the search is refused (400), never cut */
export const ARCHIVE_SEARCH_MAX_RESULTS = 20000;

/**
 * The query string of `GET /search`. Every key is optional, and the list is CLOSED: any other key is
 * answered 400.
 *
 * ⚠️ OMIT A FILTER, NEVER SEND IT EMPTY. ApiService drops `undefined` and `null` only, and an empty
 * string on `company`, `supplierId`, a date or `properties` is a 400.
 *
 * The lists travel as repeated keys (`areaIds=a&areaIds=b`), which is how Angular serializes an
 * array; the back-end reads that shape too. Ids in DIFFERENT lists combine with AND, ids in the same
 * list add up.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveSearchParams {
  /**
   * Ids of properties, or `["all"]` alone. Absent means every property the caller may search, like
   * `all`. A property out of the caller's reach is ignored without an error.
   */
  properties?: string[];
  /**
   * Company whose properties `all` stands for. Read for `admin` and `operator` only, and only when
   * `properties` is `all` or absent: with ids, the ids are the scope.
   */
  company?: string;
  /**
   * Areas of the new taxonomy. The area «Contratti forniture e servizi» (`supplyAndServiceContracts`)
   * also reaches every contract type filed in any OTHER area, as «Contratti» does in the legacy page.
   * «Contratti di locazione» is an ordinary area of the administrative group: never send it to mean
   * «Contratti».
   */
  areaIds?: string[];
  /** Sections of the new taxonomy. The section of «Contratti forniture e servizi» widens the same way */
  contentIds?: string[];
  /** Document types of the new taxonomy */
  documentTemplateDocIds?: string[];
  /** Supplier the documents were filed with: an id of the SUPPLIERS back-end */
  supplierId?: string;
  /** First day of the document date, `YYYY-MM-DD`, a Rome calendar day, included */
  startDate?: string;
  /** Last day of the document date, `YYYY-MM-DD`, a Rome calendar day, included */
  endDate?: string;
  /** Text looked for inside `data.protocollo`, case ignored, taken literally; at most 100 characters */
  protocollo?: string;
  /** Text looked for inside `data.keyword`, case ignored, taken literally; at most 100 characters */
  keyword?: string;
}

/**
 * One node of the taxonomy, as a search row and the filters name it: empty `title` and `null`
 * `translation` when the back-end could not resolve it.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveSearchLabel {
  _id: string;
  title: string;
  translation: DocumentTemplateTranslation | null;
}

/**
 * The area of a row, with the `group` the archive sorts the areas into: `amministrativo`,
 * `contratti`, `tecnico`, `unitaImmobiliari`.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveSearchArea extends DocumentArchiveSearchLabel {
  group: string;
}

/**
 * The property of a row, with what the legacy page printed of it. Empty strings when absent.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveSearchProperty {
  _id: string;
  name: string;
  code: string;
  masterCode: string;
}

/**
 * One row found: the row exactly as the archive listing serves it, plus what the search page prints
 * around it.
 *
 * It carries `data` whole, so the download works from the row alone. Every other action of the menu
 * needs the place of the row in the tree of its property (its document type, its section, the other
 * rows sharing its barcode), which this row does not carry: the page loads that tree on click.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveSearchRow extends DocumentArchiveDocListItem {
  property: DocumentArchiveSearchProperty;
  /** `code` of the company managing the property; empty when it has none */
  companyCode: string;
  /**
   * The document type, by label only: NO `dataTypeId`, so the data entry form of the row is only
   * known through the tree.
   */
  documentType: DocumentArchiveSearchLabel;
  section: DocumentArchiveSearchLabel;
  area: DocumentArchiveSearchArea;
  /** CURRENT name of the element the row hangs from; empty for a document of the property */
  structureObjectName: string;
  /**
   * Name of the supplier the document was filed with: the current one given by the suppliers
   * back-end, or the one stored on the row when that service gave none. Empty when there is neither.
   */
  supplierName: string;
  /**
   * «Data di carico», ISO 8601: when the file arrived. ABSENT where it never did, and on every row
   * copied before the switch to the new archive, until the migration of the cut-over fills it.
   * The listing, the tree and the detail serve it too; only this page reads it so far.
   */
  loaded?: string;
}

/**
 * Answer of a search: every row found, never more than ARCHIVE_SEARCH_MAX_RESULTS, sorted by
 * property name, then document type, then date of insertion.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveSearchResult {
  items: DocumentArchiveSearchRow[];
  totalCount: number;
}

/**
 * A document type as the filters offer it.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveSearchTaxonomyDoc extends DocumentArchiveSearchLabel {
  /** The obligation the taxonomy declares for the type, the same for every reader */
  mandatory: boolean;
  /**
   * True for the two optional types the legacy page writes in red anyway: PA31 and PA32, the periodic
   * renewal of the fire safety conformity and its sworn statement. Red = `mandatory || highlighted`;
   * "Selezionare solo gli obbligatori" reads `mandatory` alone.
   */
  highlighted: boolean;
}

/**
 * A section of the taxonomy, with its document types.
 *
 * The section of «Contratti forniture e servizi» lists, after its own types, the contract types of
 * every other section, with the SAME `_id`: the same type in two places.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveSearchTaxonomyContent extends DocumentArchiveSearchLabel {
  documents: DocumentArchiveSearchTaxonomyDoc[];
}

/**
 * An area of the taxonomy, with its sections.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveSearchTaxonomyArea extends DocumentArchiveSearchArea {
  contents: DocumentArchiveSearchTaxonomyContent[];
}

/**
 * The taxonomy behind the filters: the live areas, sections and types in the order of the taxonomy,
 * without empty nodes and without what the guest rules withhold from the reader.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveSearchTaxonomy {
  areas: DocumentArchiveSearchTaxonomyArea[];
}
